<template>
  <div class="main">
    <div class="body-content">
      <div v-if="isLoading">Loading Component list...</div>
      <div v-else-if="error">Error loading Component list: {{ error }}</div>
      <div v-else>
        <div class="controls">
          <div class="search-container">
            <input v-model="searchQuery" placeholder="Search..." />
            <i class="fas fa-search search-icon"></i>
          </div>
          <h1 class="title">Component List</h1>
          <div class="actions">
            <div class="tooltip-container" @mouseenter="showTooltip" @mouseleave="hideTooltip">
              <button class="add-button" @click="openAddOverlay" title="Add New Component"
                :disabled="selectedComponent || isSubmitting">
                <i class="fas fa-plus"></i>
                <div v-if="showAddTooltip" class="tooltip">Add New Component</div>
              </button>
            </div>
            <div class="export-container">
              <button class="export-button" @click="toggleExportOptions" title="Export" :disabled="isSubmitting">
                <i class="fas fa-ellipsis-v"></i>
              </button>
              <ul v-if="showExportOptions" class="export-options">
                <li @click="exportToCSV">Export to CSV</li>
                <li @click="exportToPDF">Export to PDF</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="component-table table-design">
            <thead>
              <tr>
                <th>
                  <span>Description</span>
                  <i class="filter-icon fas fa-sort" @click="sortByColumn('description')"></i>
                </th>
                <th>
                  <span>Short Name</span>
                  <i class="filter-icon fas fa-sort" @click="sortByColumn('shortName')"></i>
                </th>
                <th>
                  <span>Status</span>
                  <i class="filter-icon fas fa-sort" @click="sortByColumn('isActive')"></i>
                </th>
                <th v-if="hasPermissions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isAddingNew" class="new-row">
                <td>
                  <input v-model="newComponent.description" placeholder="Description *" class="required-field" />
                </td>
                <td><input v-model="newComponent.shortName" placeholder="Short Name" /></td>
                <td>
                  <select v-model="newComponent.isActive">
                    <option :value="0">Active</option>
                    <option :value="1">Inactive</option>
                  </select>
                </td>
                <td v-if="hasPermissions">
                  <button @click="saveComponent" class="save-button" :disabled="isSubmitting"><i class="fas fa-save"></i></button>
                  <button @click="cancelAdd" class="cancel-button" :disabled="isSubmitting"><i class="fas fa-times"></i></button>
                </td>
              </tr>
              <tr v-for="component in paginatedComponents" :key="component.autoid"
                :class="{ 'editing': component === selectedComponent }">
                <td class="row-edit" v-if="component === selectedComponent">
                  <input v-model="newComponent.description" placeholder="Description *" class="required-field" />
                </td>
                <td v-else>{{ component.description }}</td>
                <td class="row-edit" v-if="component === selectedComponent">
                  <input v-model="newComponent.shortName" placeholder="Short Name" />
                </td>
                <td v-else>{{ component.shortName }}</td>
                <td class="row-edit" v-if="component === selectedComponent">
                  <select v-model="newComponent.isActive">
                    <option :value="0">Active</option>
                    <option :value="1">Inactive</option>
                  </select>
                </td>
                <td v-else>{{ component.isActive==0 ? 'Active' : 'Inactive' }}</td>
                <td v-if="hasPermissions">
                  <button v-if="component === selectedComponent" @click="saveComponent(component)"
                    class="save-button" :disabled="isSubmitting"><i class="fas fa-save"></i></button>
                  <button v-if="component === selectedComponent" @click="cancelEdit" class="cancel-button" :disabled="isSubmitting"><i
                      class="fas fa-times"></i></button>
                  <button v-else @click="editComponent(component)" class="edit-button" :disabled="isSubmitting"><i
                      class="fas fa-edit"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="bottom-controls">
          <div class="rows-per-table">
            <button v-for="option in [10, 20, 30]" :key="option" :class="{ 'active': rowsPerPage === option }"
              @click="setRowsPerPage(option)">
              {{ option }}
            </button>
          </div>
          <div class="pagination">
            <button @click="goToPage(currentPage - 1)" :disabled="!showPrevious"> &lt; </button>
            <button v-for="page in paginatedPages" :key="page" @click="goToPage(page)"
              :class="{ active: page === currentPage }">
              {{ page }}
            </button>
            <button @click="goToPage(currentPage + 1)" :disabled="!showNext"> &gt; </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  data() {
    return {
      components: [],
      descriptionError: '',
      isLoading: false,
      error: null,
      hasPermissions: false,
      searchQuery: '',
      rowsPerPage: 10,
      currentPage: 1,
      isAddingNew: false,
      selectedComponent: null,
      showExportOptions: false,
      isSubmitting: false,
      validationTimeout: null,
      newComponent: {
        autoid: 0,
        description: '',
        shortName: '',
        isActive: 0,
        created_by: 0,
        CreateDate: '',
        last_modified_by: 0,
        ModiDate: '',
      },
      sortDirection: {
        description: 'asc',
        shortName: 'asc',
        isActive: 'asc',
      },
      showAddTooltip: false,
    };
  },
  created() {
    this.fetchComponents();
    this.checkPermissions();
  },
  computed: {
    resetComponent() {
      return {
        autoid: 0,
        description: '',
        shortName: '',
        isActive: 0,
        created_by: 0,
        CreateDate: '',
        last_modified_by: 0,
        ModiDate: '',
      };
    },
    filteredComponents() {
      return this.components
        .filter(component => {
          return Object.values(component).some(value =>
            String(value).toLowerCase().includes(this.searchQuery.toLowerCase())
          );
        })
        .sort((a, b) => {
          for (const key in this.sortDirection) {
            if (a[key] < b[key]) return this.sortDirection[key] === 'asc' ? -1 : 1;
            if (a[key] > b[key]) return this.sortDirection[key] === 'asc' ? 1 : -1;
          }
          return 0;
        });
    },
    paginatedComponents() {
      const start = (this.currentPage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      return this.filteredComponents.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredComponents.length / this.rowsPerPage);
    },
    paginatedPages() {
      const totalPages = this.totalPages;
      const currentPage = this.currentPage;
      const maxPagesToShow = 10;
      let pages = [];

      if (totalPages <= maxPagesToShow) {
        pages = Array.from({ length: totalPages }, (_, i) => i + 1);
      } else {
        pages.push(1);
        let start = Math.max(2, currentPage - 4);
        let end = Math.min(totalPages - 1, currentPage + 4);

        if (start > 2) {
          pages.push('...');
        }

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (end < totalPages - 1) {
          pages.push('...');
        }

        if (totalPages > 1) {
          pages.push(totalPages);
        }
      }

      return pages;
    },
    showPrevious() {
      return this.currentPage > 1;
    },
    showNext() {
      return this.currentPage < this.totalPages;
    }
  },
  watch: {
    'newComponent.description'(newDescription) {
      // Debounce the validation check
      if (this.validationTimeout) {
        clearTimeout(this.validationTimeout);
      }
      
      this.validationTimeout = setTimeout(() => {
        if (this.isAddingNew || this.selectedComponent) {
          this.checkComponentDescription(newDescription);
        }
      }, 300);
    },
    searchQuery() {
      this.currentPage = 1;
    },
  },
  methods: {
    goToPage(page) {
      if (page === '...' || page === this.currentPage) return;
      this.currentPage = page;
    },
    showTooltip() {
      this.showAddTooltip = true;
    },
    hideTooltip() {
      this.showAddTooltip = false;
    },
    async checkComponentDescription(description) {
      if (description.trim() === '') {
        this.descriptionError = '';
        return;
      }

      try {
        // For editing, exclude the current component being edited from duplicate check
        const currentId = this.isAddingNew ? null : this.selectedComponent?.autoid;
        
        const componentExists = this.components.some(component => 
          component.description.toLowerCase().trim() === description.toLowerCase().trim() &&
          component.autoid !== currentId
        );

        if (componentExists) {
          this.descriptionError = 'duplicate';
          useToast().error('Component description already exists.');
        } else {
          this.descriptionError = '';
        }
      } catch (err) {
        console.error('Error checking component description:', err);
        useToast().error('Error validating component description.');
      }
    },
    getCurrentUserId() {
      const user = JSON.parse(localStorage.getItem('user'));
      return user ? user.user_id : 0;
    },
    toggleExportOptions() {
      if (this.isSubmitting) return;
      this.showExportOptions = !this.showExportOptions;
      document.addEventListener('click', this.handleOutsideClick);
    },
    handleOutsideClick(event) {
      if (!event.target.closest('.export-container')) {
        this.showExportOptions = false;
        document.removeEventListener('click', this.handleOutsideClick);
      }
    },
    async fetchComponents() {
      this.isLoading = true;
      this.error = null;

      const token = localStorage.getItem('access_token');
      if (!token) {
        this.error = 'Authentication token not found';
        useToast().error('Authentication token not found.');
        this.isLoading = false;
        return;
      }

      try {
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const response = await axios.get('/component_list', config);
        this.components = response.data[2];
        localStorage.setItem('components', JSON.stringify(response.data[2]));
      } catch (error) {
        console.error('Error fetching component list:', error);
        this.error = 'Error fetching component list. Check console for details.';
        useToast().error(error.response?.data?.message || 'Error fetching component list.');
      } finally {
        this.isLoading = false;
      }
    },
    async checkPermissions() {
      this.hasPermissions = true; // Set this according to your logic
    },
    openAddOverlay() {
      if (this.isSubmitting) return;
      
      if (this.selectedComponent) {
        this.cancelEdit(); // Cancel editing if a component is currently selected
      }
      
      // Clear any existing validation timeout
      if (this.validationTimeout) {
        clearTimeout(this.validationTimeout);
        this.validationTimeout = null;
      }
      
      this.isAddingNew = true; // Open add overlay
      this.descriptionError = '';
      this.resetNewComponent(); // Reset new component fields
    },
    async saveComponent() {
      if (this.isSubmitting) return;
      
      const toast = useToast();
      
      // Validate mandatory fields
      if (!this.newComponent.description.trim()) {
        toast.error('Description is mandatory.');
        return;
      }
      
      if (this.descriptionError === 'duplicate') {
        toast.error('Please fix the duplicate description error before saving.');
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          toast.error('Authentication token not found.');
          return;
        }
        
        const userId = this.getCurrentUserId();
        const now = new Date().toISOString();

        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const payload = {
          ...this.newComponent,
          created_by: userId,
          last_modified_by: userId,
          CreateDate: now,
          ModiDate: now,
        };

        if (this.isAddingNew) {
          await axios.post('/create_component', payload, config);
          toast.success('Component Created Successfully!');
        } else if (this.selectedComponent) {
          await axios.put(`/update_component/${this.selectedComponent.autoid}`, payload, config);
          toast.success('Component Updated Successfully!');
        }

        this.fetchComponents();
        this.isAddingNew = false;
        this.selectedComponent = null;
        this.newComponent = this.resetComponent;
      } catch (error) {
        console.error('Error saving component:', error);
        toast.error(error.response?.data?.message || 'Error saving component.');
      } finally {
        this.isSubmitting = false;
      }
    },
    resetNewComponent() {
      this.newComponent = {
        autoid: 0,
        description: '',
        shortName: '',
        isActive: 0,
        created_by: 0,
        CreateDate: '',
        last_modified_by: 0,
        ModiDate: '',
      };
    },
    cancelAdd() {
      if (this.isSubmitting) return;
      
      // Clear any existing validation timeout
      if (this.validationTimeout) {
        clearTimeout(this.validationTimeout);
        this.validationTimeout = null;
      }
      
      this.isAddingNew = false;
      this.selectedComponent = null;
      this.descriptionError = '';
    },
    editComponent(component) {
      if (this.isSubmitting) return;
      
      if (this.isAddingNew) {
        this.cancelAdd(); // Cancel adding if currently adding
      }
      
      // Clear any existing validation timeout
      if (this.validationTimeout) {
        clearTimeout(this.validationTimeout);
        this.validationTimeout = null;
      }
      
      this.selectedComponent = component;
      this.newComponent = { ...component };
      this.descriptionError = '';
    },
    async cancelEdit() {
      if (this.isSubmitting) return;
      
      // Clear any existing validation timeout
      if (this.validationTimeout) {
        clearTimeout(this.validationTimeout);
        this.validationTimeout = null;
      }
      
      this.selectedComponent = null;
      this.descriptionError = '';
      this.resetNewComponent();
    },
    exportToCSV() {
      if (this.isSubmitting) return;
      
      try {
        const csvContent = [
          ['Description', 'Short Name', 'Status'],
          ...this.components.map(component => [component.description, component.shortName, component.isActive==0 ? 'Active' : 'Inactive']),
        ]
          .map(e => e.join(","))
          .join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'component_list.csv';
        link.click();
        
        useToast().success('CSV exported successfully!');
      } catch (error) {
        console.error('Error exporting CSV:', error);
        useToast().error('Error exporting CSV.');
      }
    },
    exportToPDF() {
      if (this.isSubmitting) return;
      // Implement PDF export logic here
      useToast().info('PDF export functionality not implemented yet.');
    },
    sortByColumn(column) {
      this.sortDirection[column] = this.sortDirection[column] === 'asc' ? 'desc' : 'asc';
    },
    setRowsPerPage(rows) {
      this.rowsPerPage = rows;
      this.currentPage = 1; // Reset to first page when changing rows per page
    },
  },
};
</script>


<style scoped>
.add-button:disabled,
.export-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.edit-button:disabled,
.save-button:disabled,
.cancel-button:disabled {
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.add-button:disabled:hover,
.export-button:disabled:hover {
  background-color: #6c757d;
}

.required-field {
  border-left: 3px solid #dc3545;
}

.required-field::placeholder {
  color: #dc3545;
  font-weight: 500;
}
</style>
